import moment from 'moment'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import request from '@/modules/base/util/request'
import { useNavigate } from 'react-router'

import lang from 'lang'
import config from 'config'
import documentExportConfig from '@/modules/document-export/config'

import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import { Checkbox, TextInput } from '@/modules/base/components/inputs'
import { DateInput, SelectInput } from '@/modules/base/components/inputs/index'
import { fetchDocumentExports } from '@/redux/ducks/documentExport/list'
import { useAccountSettings } from '@/modules/settings/components/tax-settings/services/account-setting-provider'
import { DatevExportType } from '@/enums/datev-export-type'
import clsx from 'clsx'
import { toast } from '@/modules/base/components/toast/toastConfig'
import getPeriodOptions from './_helpers/getPeriodOptions'
import { FinanceFormKeys } from '@/modules/document-export/constants'
import { SubTitle, Title } from '@/modules/base/components/texts'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import { errorCodes } from '@/modules/base/util/constants'
import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'
import settingsConfig from '@/modules/settings/config'

import AddIcon from '@assets/images/icons/add.svg'
import ArrowLeftAndRightIcon from '@assets/images/icons/arrow_left_and_right.svg'
import DatevIcon from '@assets/images/tax-export-logos/datev.svg'
import AgendaIcon from '@assets/images/tax-export-logos/agenda.svg'
import SimbaIcon from '@assets/images/tax-export-logos/simba.svg'
import AddisonIcon from '@assets/images/tax-export-logos/addison.svg'
import StotaxIcon from '@assets/images/tax-export-logos/stotax.svg'

const { dateFormat: { api: apiDateFormat } } = config
const defaultStartDate = moment().startOf('year').format(apiDateFormat)
const defaultEndDate = moment().endOf('year').format(apiDateFormat)

const useStyles = makeStyles((theme) => ({
  selectInput: {
    width: 250,
    zIndex: 100,
  },
  textInput: {
    width: 250,
  },
  dateInput: {
    width: 250,
  },
  startDateInput: {
    width: '50%',
  },
  endDateInput: {
    width: '50%',
  },
  action: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingTop: 30,
  },
  inputRowEnd: {
    justifyContent: 'flex-end',
  },
  inputFields: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 18,
    rowGap: 20,
    flexWrap: 'wrap',
  },
  inputFieldsWithoutWrap: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 18,
    rowGap: 20,
  },
  checkboxLabel: {
    textAlign: 'left',
    paddingLeft: 20,
  },
  checkboxItem: {
    paddingTop: 10,
  },
  subTitle: {
    lineHeight: 1.86,
    color: theme.black,
    marginBottom: 10,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  iconGroup: {
    display: 'flex',
    gap: 20,
    '& svg': {
      height: 30,
      width: 'auto',
    },
  },
  receiptTitle: {
    marginTop: 20,
  },
}))

const DocumentExportFormDatev = ({ client, readOnly }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { beginningOfFiscalYear: initialFiscalYear, refresh } = useAccountSettings()
  const datevExportValidationInfo = []
  const { taxAccountant } = useSelector(state => state.documentExport.list)
  const isOfflineClient = client?.inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE
  const [state, setState] = useState({
    ...taxAccountant,
    period: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    beginningOfFiscalYear: initialFiscalYear && moment(initialFiscalYear),
  })

  useEffect(() => {
    setState(state => ({ ...state, beginningOfFiscalYear: initialFiscalYear && moment(initialFiscalYear) }))
  }, [initialFiscalYear])

  useEffect(() => {
    setState({ ...state, ...taxAccountant })
  }, [taxAccountant])

  useEffect(() => {
    if (((client && client.isAllowed) || !client) && !isOfflineClient) {
      refresh()
    }
  }, [client])

  const handlePeriodChange = (option) => {
    const { value, startDate, endDate } = option
    setState({ ...state, period: value, startDate, endDate })
  }

  const handleBeginningOfYearChange = (value) => {
    setState({ ...state, beginningOfFiscalYear: value })
  }

  const handleConsultantChange = (value) => {
    setState({ ...state, consultantId: value })
  }

  const handleClientChange = (value) => {
    setState({ ...state, clientId: value })
  }

  const handleStartDateChange = (value) => {
    const { endDate } = state

    const newState = {
      startDate: value,
    }

    if (moment(value).isAfter(endDate)) {
      newState.endDate = value
    }

    setState({ ...state, ...newState })
  }

  const handleEndDateChange = (value) => {
    const { startDate } = state

    const newState = {
      endDate: value,
    }

    const epochTimeStamp = '1970-01-01'

    if (moment(value).isAfter(epochTimeStamp) && moment(startDate).isAfter(value)) {
      newState.startDate = value
    }

    setState({ ...state, ...newState })
  }

  const updateTaxAccountant = () => {
    const { clientId, consultantId } = state
    request(settingsConfig.taxAccountantSettings, {
      method: 'POST',
      auth: true,
      data: { clientId, consultantId },
    })
  }

  const handleCreateClick = () => {
    const { period, startDate, endDate, beginningOfFiscalYear, clientId, consultantId } = state
    const headers = {}
    if (client?.id) headers['collaboration-id'] = client.id

    const exportType = filter.length === 0 ? 'DOCUMENTS' : 'DATEV'

    request(documentExportConfig.accountantExportUrl, {
      auth: true,
      method: 'POST',
      data: {
        startDate: moment(startDate).format(apiDateFormat),
        endDate: moment(endDate).format(apiDateFormat),
        exportPeriod: period,
        exportType,
        filter,
        beginningOfFiscalYear,
        clientId,
        consultantId,
        includeReceipts: selectedReceiptTypes,
      },
      headers,
    })
      .then(() => {
        if (!taxAccountant.clientId && !taxAccountant.consultantId) {
          updateTaxAccountant()
        }
        toast.showToast({ message: lang.documentExport.createSuccess })
        navigate('/document-export#exportList', { replace: true })
        dispatch(fetchDocumentExports(client?.id))
      })
      .catch(error => {
        if (error.statusCode === 400 && error.body) {
          const { body: { name, meta } } = error
          const errorMessage = meta?.[0]?.BadRequest?.[0].message
          if (name === errorCodes.FINANCE_API_ERROR && errorMessage) {
            const hasClientError = errorMessage.includes('clientId')
            const hasConsultantError = errorMessage.includes('consultantId')
            const message = lang.documentExport.createError
              + (hasClientError && !hasConsultantError ? lang.documentExport.hasClientError : '')
              + (!hasClientError && hasConsultantError ? lang.documentExport.hasConsultantError : '')
              + (hasClientError && hasConsultantError ? lang.documentExport.hasConsultantClientError : '')
            toast.showToast({ type: 'error', message })
            return
          }
        }

        toast.showToast({ type: 'error', message: lang.documentExport.createError })
      })
  }

  const availableContent = [
    DatevExportType.invoices,
    DatevExportType.expenses,
    DatevExportType.bankBookings,
    DatevExportType.customersVendors,
  ]

  const updateSelectedContent = (content) => {
    if (!availableContent.includes(content)) return

    if (filter.includes(content)) {
      setSelectedContent([...filter.filter(filter => filter !== content)])
    } else {
      setSelectedContent([...filter, content])
    }
  }

  const [filter, setSelectedContent] = useState([
    DatevExportType.invoices,
    DatevExportType.expenses,
    DatevExportType.bankBookings,
    DatevExportType.customersVendors,
  ])

  const availableReceiptTypes = ['invoices', 'revenues', 'expenses']

  const [selectedReceiptTypes, setSelectedReceiptTypes] = useState([
    'invoices',
    'revenues',
    'expenses',
  ])

  const updateSelectedReceiptTypes = (receiptType) => {
    if (!availableReceiptTypes.includes(receiptType)) return

    if (selectedReceiptTypes.includes(receiptType)) {
      setSelectedReceiptTypes([...selectedReceiptTypes.filter(type => type !== receiptType)])
    } else {
      setSelectedReceiptTypes([...selectedReceiptTypes, receiptType])
    }
  }

  const contentCheckboxes = availableContent.map(item => (
    <div className={classes.checkboxItem}>
      <Checkbox
        key={item}
        name={item}
        checked={filter.includes(item)}
        onChange={() => updateSelectedContent(item)}
      >
        <span className={classes.checkboxLabel}>{lang.documentExport.content[item]}</span>
      </Checkbox>
    </div>

  ))

  const receiptTypeLabels = {
    invoices: lang.documentExport.content.invoices,
    revenues: lang.documentExport.content.revenue,
    expenses: lang.documentExport.content.expense,
  }

  const receiptTypeCheckboxes = availableReceiptTypes.map(receiptType => (
    <div className={classes.checkboxItem} key={receiptType}>
      <Checkbox
        name={receiptType}
        checked={selectedReceiptTypes.includes(receiptType)}
        onChange={() => updateSelectedReceiptTypes(receiptType)}
      >
        <span className={classes.checkboxLabel}>{receiptTypeLabels[receiptType]}</span>
      </Checkbox>
    </div>
  ))

  const datevContent = (
    <>
      <Title size='medium16' marginBottom='0'>{lang.documentExport.forms.exportDatev.list}</Title>
      {datevExportValidationInfo.length ? datevExportValidationInfo : contentCheckboxes}
      <Title size='medium16' marginBottom='0' className={classes.receiptTitle}>{lang.documentExport.content.documents}</Title>
      {receiptTypeCheckboxes}
    </>
  )

  const { period, startDate, endDate, beginningOfFiscalYear, consultantId, clientId } = state

  const hasBookingEntriesSelected = filter.length > 0
  const hasReceiptTypesSelected = selectedReceiptTypes.length > 0
  const hasAnySelection = hasBookingEntriesSelected || hasReceiptTypesSelected

  // Zeitraum ist immer verpflichtend
  const isPeriodInvalid = !period || (period === 'custom' && (!startDate || !endDate))

  // Wenn Buchungssätze ausgewählt sind, sind auch Beraternummer, Mandantennummer und Beginn des Wirtschaftsjahres verpflichtend
  const isDatevFieldsInvalid = hasBookingEntriesSelected && (!state?.clientId || !state?.consultantId || !state?.beginningOfFiscalYear)

  const submitButtonDisabled = isPeriodInvalid || !hasAnySelection || isDatevFieldsInvalid
  const periodOptions = getPeriodOptions()
  const customDateInputs = period === 'custom'
    ? (
      <div className={classes.inputFieldsWithoutWrap}>
        <DateInput
          name='startDate'
          label='Startdatum'
          value={startDate}
          readOnly={readOnly}
          customClass={classes.startDateInput}
          onChange={handleStartDateChange}
        />
        <DateInput
          name='endDate'
          label='Enddatum'
          value={endDate}
          readOnly={readOnly}
          customClass={classes.endDateInput}
          onChange={handleEndDateChange}
        />
      </div>
    )
    : null

  const controls = client?.isAllowed || !client
    ? (
      <>
        <SubTitle className={classes.subTitle}>{client
          ? lang.documentExport.forms.exportDatev.subTitleTaxConsultant
          : <span dangerouslySetInnerHTML={{ __html: lang.documentExport.forms.exportDatev.subTitle('/document-export#export') }} />
        }</SubTitle>
        {datevContent}
        <div className={classes.inputRow}>
          <div className={classes.inputFields}>
            <SelectInput
              notAsync
              allowCreate={false}
              label={lang.periodLabel}
              value={period}
              readOnly={readOnly}
              options={periodOptions}
              containerClass={classes.selectInput}
              customSelectOptions={{
                placeholder: 'Zeitraum wählen',
              }}
              handleChange={handlePeriodChange}
            />
            {customDateInputs}
          </div>
        </div>
        <div className={classes.inputRow}>
          <div className={classes.inputFields}>
            <TextInput
              className={classes.textInput}
              minLength={4}
              maxLength={7}
              label={lang.settings.taxes.consultantLabel}
              value={consultantId}
              onChange={handleConsultantChange}
              useFormik={false}
            />
            <TextInput
              className={classes.textInput}
              minLength={1}
              maxLength={5}
              label={lang.settings.taxes.clientLabel}
              value={clientId}
              onChange={handleClientChange}
              useFormik={false}
            />
            <DateInput
              name='beginningOfFiscalYear'
              label={lang.settings.taxes.beginningOfFiscalYearLabel}
              format='DD.MM'
              placeholder='TT.MM'
              readOnly={readOnly}
              value={beginningOfFiscalYear}
              onChange={handleBeginningOfYearChange}
              className={classes.dateInput}
              required
            />
          </div>
        </div>
        <div className={clsx(classes.inputRow, classes.inputRowEnd)}>
          <div className={classes.action}>
            <ButtonIconSquare
              disabled={submitButtonDisabled || readOnly}
              icon={AddIcon}
              label='Erstellen'
              onClick={handleCreateClick}
            />
          </div>
        </div>
      </>
    )
    : (<>{lang.clientDetail.widgets.notAllowed}</>)
  return (
    <div className={classes.container}>
      <div className={classes.titleContainer}>
        <Title icon={ArrowLeftAndRightIcon}>{lang.documentExport.forms.exportDatev.title}</Title>
        <div className={classes.iconGroup}>
          <SvgIcon svg={DatevIcon} />
          <SvgIcon svg={AgendaIcon} />
          <SvgIcon svg={SimbaIcon} />
          <SvgIcon svg={AddisonIcon} />
          <SvgIcon svg={StotaxIcon} />
        </div>
      </div>
      {controls}
    </div>
  )

}

DocumentExportFormDatev.financeFormKey = FinanceFormKeys.exportDatev

export default DocumentExportFormDatev
